import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  FileCheck,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHelp from "../components/PageHelp";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { cn, formatRelativeTime } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

const CHANNEL_OPTIONS = [
  { value: "email", label: "Email", icon: Mail },
  { value: "sms", label: "SMS", icon: Phone },
  { value: "email_sms", label: "Email + SMS", icon: Send },
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
];

// Default countries (fallback if no policies exist)
const DEFAULT_COUNTRY_OPTIONS = [
  { value: "NG", label: "Nigeria" },
  { value: "KE", label: "Kenya" },
  { value: "GH", label: "Ghana" },
  { value: "ZA", label: "South Africa" },
  { value: "TZ", label: "Tanzania" },
];

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-800",
  sent: "bg-blue-100 text-blue-800",
  opened: "bg-purple-100 text-purple-800",
  in_progress: "bg-amber-100 text-amber-800",
  submitted: "bg-emerald-100 text-emerald-800",
  expired: "bg-red-100 text-red-800",
};

export function Invitations() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [countryOptions, setCountryOptions] = useState(DEFAULT_COUNTRY_OPTIONS);
  const { hasPermission } = useAuth();

  const [formData, setFormData] = useState({
    candidate_name: "",
    candidate_email: "",
    candidate_phone: "",
    country: "NG",
    language: "en",
    channel: "email",
    message: "",
  });

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
      toast.error("Failed to load invitations");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryPolicies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/policies`);
      if (response.data && response.data.length > 0) {
        const options = response.data.map((p) => ({
          value: p.country_code,
          label: p.country_name,
        }));
        setCountryOptions(options);
        // Update default country to first policy if current default not in list
        if (
          !options.find((o) => o.value === formData.country) &&
          options.length > 0
        ) {
          setFormData((prev) => ({ ...prev, country: options[0].value }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch country policies:", error);
      // Keep default options on error
    }
  };

  useEffect(() => {
    fetchInvitations();
    fetchCountryPolicies();
  }, []);

  const resetForm = () => {
    setFormData({
      candidate_name: "",
      candidate_email: "",
      candidate_phone: "",
      country: "NG",
      language: "en",
      channel: "email",
      message: "",
    });
  };

  const handleSubmit = async () => {
    if (!formData.candidate_name) {
      toast.error("Candidate name is required");
      return;
    }

    if (formData.channel === "email" && !formData.candidate_email) {
      toast.error("Email is required for email invitations");
      return;
    }

    if (formData.channel === "sms" && !formData.candidate_phone) {
      toast.error("Phone number is required for SMS invitations");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/invitations`, formData);

      const verifyUrl = response.data.send_result?.verify_url;

      if (response.data.send_result?.success) {
        toast.success("Invitation sent successfully!");
      } else {
        toast.info(
          `Invitation created. ${response.data.send_result?.error || "Send manually using the link."}`,
        );
      }

      // Show the verification URL
      if (verifyUrl) {
        toast.info(
          <div>
            <p className="font-medium">Verification Link:</p>
            <p className="text-xs break-all mt-1">{verifyUrl}</p>
          </div>,
          { duration: 10000 },
        );
      }

      setDialogOpen(false);
      resetForm();
      fetchInvitations();
    } catch (error) {
      console.error("Failed to create invitation:", error);
      toast.error(
        error.response?.data?.detail || "Failed to create invitation",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (invitationId) => {
    try {
      await axios.post(`${API_URL}/api/invitations/${invitationId}/resend`);
      toast.success("Invitation resent");
      fetchInvitations();
    } catch (error) {
      console.error("Failed to resend:", error);
      toast.error("Failed to resend invitation");
    }
  };

  const handleDelete = async (invitationId) => {
    try {
      setDeletingId(invitationId);
      await axios.delete(`${API_URL}/api/invitations/${invitationId}`);
      toast.success("Invitation deleted");
      fetchInvitations();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error(
        error.response?.data?.detail || "Failed to delete invitation",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const copyVerifyLink = (token) => {
    const url = `${FRONTEND_URL}/verify/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(token);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getChannelInfo = (channel, preferredContactMethod) => {
    // Check if email_sms was used (stored in preferred_contact_method)
    const actualChannel =
      preferredContactMethod === "email_sms" ? "email_sms" : channel;
    const opt = CHANNEL_OPTIONS.find((c) => c.value === actualChannel);
    return {
      icon: opt?.icon || Mail,
      label: opt?.label || channel,
    };
  };

  return (
    <div className="p-6 space-y-6" data-testid="invitations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            Invitations
          </h1>
          <p className="text-muted-foreground mt-1">
            Invite candidates to start their identity verification process
          </p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="invitations" />
          <Button variant="outline" size="sm" onClick={fetchInvitations}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="create-invitation-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Invitation
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Invite Candidate</DialogTitle>
                <DialogDescription>
                  Send a verification invitation via email or SMS
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Candidate Name *</Label>
                  <Input
                    placeholder="John Doe"
                    value={formData.candidate_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        candidate_name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select
                      value={formData.country}
                      onValueChange={(v) =>
                        setFormData({ ...formData, country: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryOptions.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(v) =>
                        setFormData({ ...formData, language: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map((language) => (
                          <SelectItem
                            key={language.value}
                            value={language.value}
                          >
                            {language.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Send Via</Label>
                    <Select
                      value={formData.channel}
                      onValueChange={(v) =>
                        setFormData({ ...formData, channel: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNEL_OPTIONS.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            <span className="flex items-center gap-2">
                              <c.icon className="h-4 w-4" />
                              {c.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div />
                </div>

                <div className="space-y-2">
                  <Label>Email {formData.channel === "email" && "*"}</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.candidate_email}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        candidate_email: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    Phone Number {formData.channel === "sms" && "*"}
                  </Label>
                  <Input
                    placeholder="+234 800 000 0000"
                    value={formData.candidate_phone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        candidate_phone: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Custom Message (Optional)</Label>
                  <Textarea
                    placeholder="Add a personalized message..."
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Invitation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sent</p>
                <p className="text-2xl font-bold">{invitations.length}</p>
              </div>
              <Send className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">
                  {
                    invitations.filter((i) =>
                      ["pending", "sent", "opened"].includes(i.status),
                    ).length
                  }
                </p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Failed/Bounced</p>
                <p className="text-2xl font-bold text-red-600">
                  {
                    invitations.filter((i) =>
                      ["failed", "bounced"].includes(i.delivery_status),
                    ).length
                  }
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">
                  {invitations.filter((i) => i.status === "in_progress").length}
                </p>
              </div>
              <Eye className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Submitted</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {invitations.filter((i) => i.status === "submitted").length}
                </p>
              </div>
              <FileCheck className="h-8 w-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invitations Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : invitations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((inv) => {
                  const channelInfo = getChannelInfo(
                    inv.channel,
                    inv.preferred_contact_method,
                  );
                  const ChannelIcon = channelInfo.icon;
                  const deliveryStatus = inv.delivery_status || "pending";
                  const deliveryColors = {
                    pending: "bg-slate-100 text-slate-700",
                    sent: "bg-blue-100 text-blue-700",
                    delivered: "bg-emerald-100 text-emerald-700",
                    bounced: "bg-red-100 text-red-700",
                    complained: "bg-orange-100 text-orange-700",
                    delayed: "bg-amber-100 text-amber-700",
                    failed: "bg-red-100 text-red-700",
                  };
                  return (
                    <TableRow key={inv.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{inv.candidate_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {inv.preferred_contact_method === "email_sms" ? (
                              <>
                                {inv.candidate_email}
                                {inv.candidate_phone && (
                                  <span className="text-xs ml-1">
                                    / {inv.candidate_phone}
                                  </span>
                                )}
                              </>
                            ) : (
                              inv.candidate_email || inv.candidate_phone
                            )}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ChannelIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{channelInfo.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>{inv.country}</TableCell>
                      <TableCell>
                        <Badge className={cn(STATUS_COLORS[inv.status])}>
                          {inv.status.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge
                            className={cn(
                              deliveryColors[deliveryStatus] ||
                                deliveryColors.pending,
                              "text-xs",
                            )}
                          >
                            {deliveryStatus}
                          </Badge>
                          {inv.delivery_bounce_reason && (
                            <span className="text-xs text-red-600">
                              {inv.delivery_bounce_reason}
                            </span>
                          )}
                          {inv.delivery_error && (
                            <span
                              className="text-xs text-red-600 truncate max-w-[120px]"
                              title={inv.delivery_error}
                            >
                              {inv.delivery_error}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatRelativeTime(inv.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyVerifyLink(inv.token)}
                            title="Copy verification link"
                          >
                            {copiedId === inv.token ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          {inv.status !== "submitted" &&
                            inv.status !== "expired" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResend(inv.id)}
                                title="Resend invitation"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                          {inv.case_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              title="View case"
                            >
                              <a href={`/cases/${inv.case_id}`}>
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          {inv.status !== "submitted" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  disabled={deletingId === inv.id}
                                  title="Delete invitation"
                                  data-testid={`delete-invitation-${inv.id}`}
                                >
                                  {deletingId === inv.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Delete Invitation?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the invitation
                                    for {inv.candidate_name}. The verification
                                    link will no longer work.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(inv.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Send className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-lg font-medium">No invitations yet</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                Start inviting candidates to verify their identity
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Invitation
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Invitations;
